import { ApiError } from "@/common/lib/api";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { useLotsList } from "@/features/lots/api/lotsApi";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ChevronLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { type ReceivedInvoiceRow, useReceivedInvoiceDetail } from "../api/receivedInvoicesApi";
import { InvoiceLinesSection } from "../components/InvoiceLinesSection";
import { DocumentTypePill, PipelineStatusPill, TaxStatusPill } from "../components/InvoicePills";
import { SplitBuilder } from "../components/SplitBuilder";
import { formatExchangeRate, formatInvoiceAmount } from "../utils/formatInvoiceAmount";
import { quarantineReasonText } from "../utils/quarantine";
import { dominantSuggestedCategory } from "../utils/suggestedCategory";

export function RecibidoDetailPage() {
  const { invoiceId = "" } = useParams<{ invoiceId: string }>();
  const { currentAccount, currentMembership } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const canAssign = currentMembership?.role === "owner" || currentMembership?.role === "admin";

  const detailQuery = useReceivedInvoiceDetail(accountId, invoiceId);
  const lotsQuery = useLotsList(accountId);

  if (detailQuery.isLoading || lotsQuery.isLoading) {
    return (
      <>
        <title>Cargando · Recibidos · HATO</title>
        <Breadcrumb />
        <DetailSkeleton />
      </>
    );
  }

  if (detailQuery.isError) {
    if (detailQuery.error instanceof ApiError && detailQuery.error.status === 404) {
      return <InvoiceNotFound />;
    }
    return (
      <>
        <title>Recibidos · HATO</title>
        <Breadcrumb />
        <div className="mt-12 flex flex-col items-center gap-4 text-center">
          <p className="text-app-ink-2 text-sm">No se pudo cargar la factura.</p>
          <button
            type="button"
            onClick={() => void detailQuery.refetch()}
            className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
          >
            Reintentar
          </button>
        </div>
      </>
    );
  }

  const detail = detailQuery.data;
  if (!detail) return <InvoiceNotFound />;

  const { invoice, lines, assignments, unassignedCents } = detail;
  const lots = lotsQuery.data?.lots ?? [];
  const assignable =
    invoice.pipelineStatus === "ready" ||
    invoice.pipelineStatus === "partially_assigned" ||
    invoice.pipelineStatus === "assigned";

  return (
    <>
      <title>{`${invoice.emisorName ?? "Recibido"} · Recibidos · HATO`}</title>
      <Breadcrumb />

      <div className="mt-4 flex flex-col gap-4">
        <header>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-medium text-2xl text-app-ink-1 tracking-tight">
              {invoice.emisorName ?? "Correo recibido"}
            </h1>
            <DocumentTypePill type={invoice.documentType} />
            <PipelineStatusPill status={invoice.pipelineStatus} />
            <TaxStatusPill status={invoice.taxStatus} />
          </div>
          {invoice.issuedAt && (
            <p className="mt-1 text-app-ink-3 text-sm">
              Emitida el {format(new Date(invoice.issuedAt), "d 'de' MMMM yyyy", { locale: es })}
            </p>
          )}
        </header>

        {invoice.pipelineStatus === "quarantined" && (
          <p
            role="alert"
            className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700"
          >
            {quarantineReasonText(invoice.quarantineReason)}
          </p>
        )}

        <InvoiceHeaderCard invoice={invoice} />

        <InvoiceLinesSection lines={lines} currency={invoice.currency} />

        {assignable && invoice.totalCents !== null && (
          <SplitBuilder
            accountId={accountId}
            invoice={invoice}
            assignments={assignments}
            unassignedCents={unassignedCents}
            lots={lots}
            suggestedCategory={dominantSuggestedCategory(lines)}
            canAssign={canAssign}
          />
        )}
      </div>
    </>
  );
}

const CONDICION_VENTA_LABEL: Record<string, string> = {
  "01": "Contado",
  "02": "Crédito",
  "03": "Consignación",
  "04": "Apartado",
  "99": "Otros",
};

const MEDIO_PAGO_LABEL: Record<string, string> = {
  "01": "Efectivo",
  "02": "Tarjeta",
  "03": "Cheque",
  "04": "Transferencia bancaria",
  "05": "Recaudado por terceros",
  "99": "Otros",
};

// Consecutivo del comprobante: chars 22-41 de la clave de 50 (mismo slice que
// consecutivoFromClave del backend).
function consecutivoFromClave(clave: string | null): string | null {
  if (clave === null || clave.length !== 50) return null;
  return clave.substring(21, 41);
}

function InvoiceHeaderCard({ invoice }: { invoice: ReceivedInvoiceRow }) {
  const consecutivo = consecutivoFromClave(invoice.clave);
  const condicionVenta = invoice.condicionVenta
    ? (CONDICION_VENTA_LABEL[invoice.condicionVenta] ?? `Código ${invoice.condicionVenta}`)
    : null;
  const medioPago = invoice.medioPago
    ? (MEDIO_PAGO_LABEL[invoice.medioPago] ?? `Código ${invoice.medioPago}`)
    : null;
  const isUsd = invoice.currency !== null && invoice.currency !== "CRC";

  return (
    <section
      aria-label="Datos de la factura"
      className="grid grid-cols-1 gap-4 rounded-2xl border border-app-border bg-app-surface p-4 sm:grid-cols-2"
    >
      <Field
        label="Total"
        value={formatInvoiceAmount(invoice.totalCents, invoice.currency)}
        accent
      />
      {invoice.taxCents !== null && (
        <Field label="IVA" value={formatInvoiceAmount(invoice.taxCents, invoice.currency)} />
      )}
      {invoice.emisorTaxId && <Field label="Cédula del emisor" value={invoice.emisorTaxId} mono />}
      {consecutivo && <Field label="# Consecutivo" value={consecutivo} mono />}
      {condicionVenta && (
        <Field
          label="Condición de venta"
          value={
            invoice.plazoCredito
              ? `${condicionVenta} (${invoice.plazoCredito} días)`
              : condicionVenta
          }
        />
      )}
      {medioPago && <Field label="Medio de pago" value={medioPago} />}
      {isUsd && (
        <Field
          label="Moneda"
          value={`${invoice.currency} · tipo de cambio ${formatExchangeRate(invoice.exchangeRate)}`}
        />
      )}
      {invoice.hasExoneration && <Field label="Exoneración" value="Trae líneas exoneradas" />}
      {invoice.haciendaMessageDetail && (
        <div className="sm:col-span-2">
          <Field
            label="Detalle del acuse de Hacienda"
            value={invoice.haciendaMessageDetail}
            multiline
          />
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  accent,
  mono,
  multiline,
}: {
  label: string;
  value: string;
  accent?: boolean;
  mono?: boolean;
  multiline?: boolean;
}) {
  return (
    <div>
      <div className="text-[11px] text-app-ink-3 uppercase tracking-wide">{label}</div>
      <div
        className={
          accent
            ? "mt-0.5 font-semibold text-app-ink-1 text-xl tabular-nums"
            : multiline
              ? "mt-0.5 whitespace-pre-wrap text-[14px] text-app-ink-1"
              : mono
                ? "mt-0.5 font-mono text-[14px] text-app-ink-1"
                : "mt-0.5 text-[14px] text-app-ink-1"
        }
      >
        {value}
      </div>
    </div>
  );
}

function Breadcrumb() {
  return (
    <nav aria-label="Migas de pan" className="flex items-center gap-2 text-[12px] text-app-ink-3">
      <Link to="/app/recibidos" className="inline-flex items-center gap-1 hover:text-app-ink-1">
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        <span>Recibidos</span>
      </Link>
    </nav>
  );
}

function InvoiceNotFound() {
  return (
    <>
      <title>Recibido no encontrado · HATO</title>
      <Breadcrumb />
      <div className="mt-12 flex flex-col items-center gap-4 text-center">
        <h1 className="font-medium text-2xl text-app-ink-1 tracking-tight">
          Factura no encontrada
        </h1>
        <p className="max-w-sm text-app-ink-3 text-sm">
          El recibido no existe o pertenece a otra cuenta.
        </p>
        <Link
          to="/app/recibidos"
          className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
        >
          Volver a Recibidos
        </Link>
      </div>
    </>
  );
}

function DetailSkeleton() {
  return (
    <div className="mt-4 space-y-4">
      <div className="h-10 w-64 animate-pulse rounded-xl bg-app-surface-2" />
      <div className="h-32 animate-pulse rounded-2xl bg-app-surface-2" />
      <div className="h-64 animate-pulse rounded-2xl bg-app-surface-2" />
    </div>
  );
}
